import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Div from '@hellodarwin/core/lib/components/common/div';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import { usePlatform } from '@hellodarwin/core/lib/features/providers/platform-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useAssistantApi } from '../../hooks/useAssistantApi';
import { useAssistantContext } from '../../providers';
import { useAssistantDispatch, useAssistantSelector, } from '../../redux/assistant-hooks';
import { selectActiveToolMessage, selectAssistantStatus, selectConversationMessages, selectMessageStreamingError, } from '../../redux/selectors';
import { deleteMessage } from '../../redux/thunks/conversationThunks';
import { AssistantMessageFormatter } from '../../services/AssistantMessageFormatter';
import { extractCharlesIdentifiedGrantsIds } from '../../utils/messageHelpers';
import { getMessageChunks, isAgentControlTool, isMessageStreaming, } from '../../utils/sseEventUtils';
import MessageBadge from '../Badge';
import { AssistantMarkdown, MessageActions, StatusIndicator } from '../common';
import { ToolCallCard } from '../ToolCallCard';
import ListScroller from './ListScroller';
import { EmptyStateContainer, MessageBubble, MessageContent, MessageGroup, MessageIdentifiedGrants, MessageListContainer, MessageListContent, MessageWrapper, StreamingMessageText, ToolCallsContainer, } from './styles';
const AssistantMessageList = ({ conversationId, grantCardByIdCtor, }) => {
    const { t } = useTranslation();
    const { app } = usePlatform();
    const theme = useTheme();
    const messages = useAssistantSelector((state) => selectConversationMessages(state, conversationId));
    const activeToolMessage = useAssistantSelector(selectActiveToolMessage);
    const streamingError = useAssistantSelector(selectMessageStreamingError);
    const { isAutoScrollEnabled } = useAssistantContext();
    const isConversationLoading = useAssistantSelector((state) => {
        return (state.assistant.status === 'loading' &&
            (!messages || messages.length === 0));
    });
    const scrollRef = useRef(null);
    const status = useAssistantSelector(selectAssistantStatus);
    if (isConversationLoading) {
        return (_jsx(MessageListContainer, { ref: scrollRef, children: _jsx(EmptyStateContainer, { children: "Loading conversation..." }) }));
    }
    if (!messages || messages.length === 0) {
        return (_jsx(MessageListContainer, { ref: scrollRef, children: _jsx(EmptyStateContainer, { children: t('assistant.no_messages') }) }));
    }
    const groupedMessages = AssistantMessageFormatter.groupMessagesByAuthor(messages);
    return (_jsx(MessageListContainer, { ref: scrollRef, children: _jsx(MessageListContent, { children: _jsxs(ListScroller, { isAutoScrollEnabled: isAutoScrollEnabled, children: [_jsx(Div, {}), groupedMessages === null || groupedMessages === void 0 ? void 0 : groupedMessages.map((group, groupIndex) => (_jsx(MessageGroup, { children: group.map((message) => (_jsx(MessageItem, { message: message, conversationId: conversationId, grantCardByIdCtor: grantCardByIdCtor }, message.id))) }, groupIndex))), _jsx(StatusIndicator, { status: status, message: activeToolMessage
                            ? t(`assistant|tool_call_${activeToolMessage}`)
                            : undefined }), streamingError && (_jsx(MessageGroup, { children: _jsx(Div, { style: {
                                padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
                                background: theme.colors.red_5,
                                border: `1px solid ${theme.colors.red_4}`,
                                borderRadius: theme.border_radius.md,
                                margin: `${theme.spacing.sm}px 0`,
                            }, children: _jsxs(Typography, { elementTheme: "body1", color: theme.colors.error, children: [app === 'client'
                                        ? t('assistant|connection_error')
                                        : streamingError.message.startsWith('assistant|') ||
                                            streamingError.message.startsWith('assistant.')
                                            ? t(streamingError.message.replace('assistant.', 'assistant|'))
                                            : streamingError.message, ' '] }) }) })), _jsx(Div, {})] }) }) }));
};
const isAgentControlToolCallMessage = (message) => {
    var _a;
    if (message.role !== 'assistant')
        return false;
    const toolCalls = (_a = message.meta) === null || _a === void 0 ? void 0 : _a.tool_calls;
    if (!toolCalls || toolCalls.length === 0)
        return false;
    return toolCalls.every((tc) => { var _a; return isAgentControlTool((_a = tc.function) === null || _a === void 0 ? void 0 : _a.name); });
};
const MessageItem = ({ message, conversationId, grantCardByIdCtor }) => {
    const assistantApi = useAssistantApi();
    const dispatch = useAssistantDispatch();
    const { app } = usePlatform();
    const { t: t2 } = useTranslations();
    const theme = useTheme();
    const handleFeedback = async (messageId, conversationId, rating, comment) => {
        try {
            const isUpvoted = rating === 'positive' ? true : false;
            await assistantApi.setMessageFeedback(messageId, conversationId, isUpvoted, comment);
        }
        catch (error) {
            console.error('Failed to set message feedback:', error);
        }
    };
    const handleDelete = async (messageId) => {
        try {
            await dispatch(deleteMessage({
                api: assistantApi,
                messageId,
                conversationId,
            })).unwrap();
        }
        catch (error) {
            console.error('Failed to delete message:', error);
        }
    };
    if (isAgentControlToolCallMessage(message)) {
        return null;
    }
    if (message.role === 'tool' && isAgentControlTool(message.author_id)) {
        return null;
    }
    const charlesIdentifiedGrantsIds = message.author_type !== 'tool'
        ? extractCharlesIdentifiedGrantsIds(message)
        : [];
    const isStreaming = isMessageStreaming(message);
    const streamedChunks = getMessageChunks(message);
    const formattedContent = AssistantMessageFormatter.formatMessageContent(message);
    const shouldAnimateBubble = message.role !== 'assistant' || streamedChunks.length === 0;
    const hasDisplayableContent = message.author_type !== 'tool' &&
        (isStreaming || (message.content && message.content.trim() !== ''));
    return (_jsx(MessageWrapper, { role: message.role, children: _jsxs(MessageContent, { role: message.role, children: [hasDisplayableContent && (_jsxs(_Fragment, { children: [_jsx(MessageBadge, { message: message }), isStreaming && message.role === 'assistant' ? (_jsx(MessageBubble, { "$role": message.role, children: _jsx(StreamingMessageText, { "aria-live": "polite", children: _jsx(AssistantMarkdown, { children: formattedContent }) }) })) : (_jsx(MessageBubble, { "$role": message.role, "$animateIn": shouldAnimateBubble, "$isStreaming": isStreaming, children: _jsx(AssistantMarkdown, { children: formattedContent }) })), message.role === 'assistant' &&
                            !isStreaming &&
                            charlesIdentifiedGrantsIds.length > 1 &&
                            app !== 'admin' && (_jsxs(MessageIdentifiedGrants, { children: [_jsx(Typography.Title, { level: 3, elementTheme: "h5", style: {
                                        fontWeight: theme.fontWeights.semibold,
                                        padding: `${theme.spacing.md}px ${theme.spacing.sm}px ${theme.spacing.sm}px ${theme.spacing.md}px`,
                                    }, children: t2('assistant|assistant_tool_search_grants_programs_identified') }), _jsx(Div, { className: "mig-content", children: charlesIdentifiedGrantsIds.map((id) => (_jsx(Div, { style: { flexShrink: 0 }, children: grantCardByIdCtor ? grantCardByIdCtor(id) : null }, id))) })] })), !isStreaming && (_jsx(MessageActions, { message: message, onFeedback: handleFeedback, onDelete: handleDelete }))] })), message.role === 'tool' &&
                    !(message.author_id === 'write_memory' && app !== 'admin') && (_jsx(ToolCallsContainer, { children: _jsx(ToolCallCard, { message: message }, message.id) }))] }) }, message.id));
};
export default AssistantMessageList;
